import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiBriefcase,
  FiCheckSquare,
  FiClock,
  FiEdit2,
  FiFlag,
  FiGitMerge,
  FiMail,
  FiMessageSquare,
  FiPhone,
  FiUser,
  FiX,
} from "react-icons/fi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fetchCrmCompanies,
  fetchCrmContact,
  fetchCrmConversations,
  fetchCrmLeads,
  fetchCrmTasks,
  unmergeCrmContact,
  updateCrmContact,
} from "../crmApi";
import CrmMergeHistory from "../components/CrmMergeHistory";
import { useCrmCopy } from "../crmI18n";
import ZadarmaCallButton from "../telephony/ZadarmaCallButton";

const openTaskStatuses = new Set(["open", "in_progress"]);
const emptyContactForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company_id: "",
  job_title: "",
  department: "",
  crm_status: "active",
  source: "",
  notes: "",
};

function buildContactForm(contact) {
  return {
    first_name: contact?.first_name || "",
    last_name: contact?.last_name || "",
    email: contact?.email || "",
    phone: contact?.phone || "",
    company_id: contact?.company_id || contact?.company?.id || "",
    job_title: contact?.job_title || "",
    department: contact?.department || "",
    crm_status: contact?.crm_status || "active",
    source: contact?.source || "",
    notes: contact?.notes || "",
  };
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
}

function getContactLabel(contact) {
  if (!contact) return "";
  return contact.full_name || [contact.first_name, contact.last_name].filter(Boolean).join(" ") || contact.email || contact.phone || "";
}

function getConversationDate(conversation) {
  return conversation?.last_message_at || conversation?.updated_at || conversation?.created_at;
}

function isOpportunityLead(lead) {
  return Boolean(lead?.opportunity_started_at);
}

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const copy = useCrmCopy();
  const contactsCopy = copy.entities.contacts;
  const detailCopy = copy.entities.contacts.detail;
  const [contact, setContact] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [leads, setLeads] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [form, setForm] = useState(emptyContactForm);
  const [unmergeSaving, setUnmergeSaving] = useState(false);
  const [error, setError] = useState("");

  const loadDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const [contactData, leadsData, opportunitiesData, conversationsData, tasksData] = await Promise.all([
        fetchCrmContact(id),
        fetchCrmLeads({ contact_customer_id: id, include_inactive: 1, limit: 100 }),
        fetchCrmLeads({ contact_customer_id: id, opportunity_only: 1, include_inactive: 1, limit: 100 }),
        fetchCrmConversations({ contact_customer_id: id, limit: 100 }),
        fetchCrmTasks({ contact_customer_id: id, limit: 100 }),
      ]);
      const leadRows = Array.isArray(leadsData?.rows) ? leadsData.rows : [];
      setContact(contactData || null);
      setLeads(leadRows.filter((lead) => !isOpportunityLead(lead)));
      setOpportunities(Array.isArray(opportunitiesData?.rows) ? opportunitiesData.rows : []);
      setConversations(Array.isArray(conversationsData?.rows) ? conversationsData.rows : []);
      setTasks(Array.isArray(tasksData?.rows) ? tasksData.rows : []);
    } catch (err) {
      console.error("[CRM] Contact detail load error:", err);
      setError(err?.response?.data?.error || detailCopy.loadError);
      setContact(null);
      setLeads([]);
      setOpportunities([]);
      setConversations([]);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [detailCopy.loadError, id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const loadCompanyOptions = useCallback(async () => {
    try {
      const data = await fetchCrmCompanies({ limit: 200 });
      setCompanies(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      console.error("[CRM] Contact detail company options error:", err);
      setCompanies([]);
    }
  }, []);

  const contactLabel = getContactLabel(contact) || detailCopy.untitledContact;
  const returnTo = location.state?.returnTo;
  const buildContactReturnState = useCallback(
    () => ({
      returnTo: {
        pathname: location.pathname,
        search: location.search,
        label: contactLabel,
      },
    }),
    [contactLabel, location.pathname, location.search]
  );
  const handleBack = () => {
    if (returnTo?.pathname) {
      navigate(`${returnTo.pathname}${returnTo.search || ""}`, {
        state: returnTo.state || undefined,
      });
      return;
    }
    navigate("/crm/contacts");
  };
  const openTasks = useMemo(
    () => tasks.filter((task) => openTaskStatuses.has(task?.status || "open")),
    [tasks]
  );
  const statusOptions = useMemo(
    () => [
      { value: "active", label: copy.common.active },
      { value: "inactive", label: copy.common.inactive },
    ],
    [copy.common.active, copy.common.inactive]
  );

  const handleFormFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openEditContact = async () => {
    if (!contact?.id) return;
    setForm(buildContactForm(contact));
    setEditModalOpen(true);
    if (!companies.length) await loadCompanyOptions();
  };

  const closeEditContact = () => {
    if (editSaving) return;
    setEditModalOpen(false);
    setForm(emptyContactForm);
  };

  const handleEditContactSubmit = async (event) => {
    event.preventDefault();
    if (!contact?.id) return;

    setEditSaving(true);
    setError("");
    try {
      await updateCrmContact(contact.id, {
        ...form,
        company_id: form.company_id || null,
      });
      await loadDetail();
      setEditModalOpen(false);
      setForm(emptyContactForm);
    } catch (err) {
      console.error("[CRM] Contact detail save error:", err);
      setError(err?.response?.data?.error || contactsCopy.saveError);
    } finally {
      setEditSaving(false);
    }
  };

  const handleUnmerge = async () => {
    if (!contact?.id || unmergeSaving) return;
    setUnmergeSaving(true);
    setError("");
    try {
      await unmergeCrmContact(contact.id);
      await loadDetail();
    } catch (err) {
      console.error("[CRM] Contact unmerge error:", err);
      setError(err?.response?.data?.error || copy.entities.contacts.merge.unmergeError);
    } finally {
      setUnmergeSaving(false);
    }
  };

  const tabs = useMemo(
    () => [
      { key: "overview", label: detailCopy.tabs.overview },
      { key: "leads", label: detailCopy.tabs.leads, count: leads.length },
      { key: "opportunities", label: detailCopy.tabs.opportunities, count: opportunities.length },
      { key: "activity", label: detailCopy.tabs.activity, count: conversations.length },
      { key: "tasks", label: detailCopy.tabs.tasks, count: tasks.length },
    ],
    [
      conversations.length,
      detailCopy.tabs.activity,
      detailCopy.tabs.leads,
      detailCopy.tabs.opportunities,
      detailCopy.tabs.overview,
      detailCopy.tabs.tasks,
      leads.length,
      opportunities.length,
      tasks.length,
    ]
  );

  return (
    <main className="crm-page">
      <header className="crm-pageHeader">
        <div>
          <p className="crm-eyebrow">{copy.sections.contacts.title}</p>
          <h1>{contactLabel}</h1>
        </div>

        <div className="crm-pageHeaderActions">
          <button type="button" className="crm-ghostButton" onClick={handleBack}>
            <FiArrowLeft aria-hidden="true" />
            <span>{returnTo?.label || detailCopy.backToContacts}</span>
          </button>
          <button type="button" className="crm-ghostButton" onClick={openEditContact} disabled={!contact || loading}>
            <FiEdit2 aria-hidden="true" />
            <span>{copy.common.edit}</span>
          </button>
          {contact?.merge_info?.can_unmerge ? (
            <button type="button" className="crm-ghostButton" onClick={handleUnmerge} disabled={unmergeSaving}>
              <FiGitMerge aria-hidden="true" />
              <span>{unmergeSaving ? copy.entities.contacts.merge.saving : detailCopy.unmerge}</span>
            </button>
          ) : null}
        </div>
      </header>

      {error ? <div className="crm-stateBanner is-error">{error}</div> : null}

      <section className="crm-contactHero">
        <div className="crm-contactAvatar">
          <FiUser aria-hidden="true" />
        </div>
        <div className="crm-contactHeroMain">
          <div className="crm-contactHeroTitle">
            <h2>{loading && !contact ? copy.common.loading : contactLabel}</h2>
            <div className="crm-contactHeroActions">
              <span className={`crm-statusPill crm-statusPill--${contact?.crm_status || "active"}`}>
                {contact?.crm_status === "inactive" ? copy.common.inactive : copy.common.active}
              </span>
              <ZadarmaCallButton
                phones={contact?.phone}
                label={contactLabel}
                entityType="contact"
                entityId={contact?.id}
              />
            </div>
          </div>
          <div className="crm-contactHeroMeta">
            <span>
              <FiMail aria-hidden="true" />
              {contact?.email || detailCopy.noEmail}
            </span>
            <span>
              <FiPhone aria-hidden="true" />
              {contact?.phone || detailCopy.noPhone}
            </span>
            <span>
              <FiBriefcase aria-hidden="true" />
              {contact?.company?.id ? (
                <button
                  type="button"
                  className="crm-inlineLinkButton"
                  onClick={() =>
                    navigate(`/crm/companies?company_id=${contact.company.id}`, {
                      state: buildContactReturnState(),
                    })
                  }
                >
                  {contact.company.name || copy.common.noCompany}
                </button>
              ) : (
                copy.common.noCompany
              )}
            </span>
          </div>
        </div>
      </section>

      <section className="crm-contactMetricGrid">
        <article className="crm-metricCard crm-contactMetric">
          <span>{detailCopy.metrics.activity}</span>
          <strong>{loading ? "-" : conversations.length}</strong>
        </article>
        <article className="crm-metricCard crm-contactMetric">
          <span>{detailCopy.metrics.openTasks}</span>
          <strong>{loading ? "-" : openTasks.length}</strong>
        </article>
        <article className="crm-metricCard crm-contactMetric">
          <span>{detailCopy.metrics.lastContact}</span>
          <strong>{loading ? "-" : formatDate(conversations[0] ? getConversationDate(conversations[0]) : contact?.last_contacted_at)}</strong>
        </article>
      </section>

      <section className="crm-panel crm-contactDetailPanel">
        <div className="crm-tabs" role="tablist" aria-label={detailCopy.tabsLabel}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={activeTab === tab.key ? "crm-tab crm-tab--active" : "crm-tab"}
              onClick={() => setActiveTab(tab.key)}
            >
              <span>{tab.label}</span>
              {typeof tab.count === "number" ? <small>{tab.count}</small> : null}
            </button>
          ))}
        </div>

        <div className="crm-tabPanel">
          {activeTab === "overview" ? (
            <OverviewTab
              contact={contact}
              copy={copy}
              detailCopy={detailCopy}
              loading={loading}
              onOpenCompany={(company) =>
                navigate(`/crm/companies?company_id=${company.id}`, {
                  state: buildContactReturnState(),
                })
              }
            />
          ) : null}
          {activeTab === "leads" ? (
            <LeadListTab
              leads={leads}
              copy={copy}
              detailCopy={detailCopy}
              loading={loading}
              emptyLabel={detailCopy.emptyLeads}
              fallbackLabel="Lead"
              onOpenLead={(lead) =>
                navigate(`/crm/leads?lead_id=${lead.id}`, {
                  state: buildContactReturnState(),
                })
              }
              onOpenCompany={(company) =>
                navigate(`/crm/companies?company_id=${company.id}`, {
                  state: buildContactReturnState(),
                })
              }
            />
          ) : null}
          {activeTab === "opportunities" ? (
            <LeadListTab
              leads={opportunities}
              copy={copy}
              detailCopy={detailCopy}
              loading={loading}
              emptyLabel={detailCopy.emptyOpportunities}
              fallbackLabel={copy.entities.leads.opportunities?.tableTitle || "Opportunity"}
              onOpenLead={(lead) =>
                navigate(`/crm/opportunities?lead_id=${lead.id}`, {
                  state: buildContactReturnState(),
                })
              }
              onOpenCompany={(company) =>
                navigate(`/crm/companies?company_id=${company.id}`, {
                  state: buildContactReturnState(),
                })
              }
            />
          ) : null}
          {activeTab === "activity" ? (
            <ActivityTab conversations={conversations} copy={copy} detailCopy={detailCopy} loading={loading} />
          ) : null}
          {activeTab === "tasks" ? (
            <TasksTab
              tasks={tasks}
              copy={copy}
              detailCopy={detailCopy}
              loading={loading}
              onOpenLead={(lead) =>
                navigate(`/crm/${isOpportunityLead(lead) ? "opportunities" : "leads"}?lead_id=${lead.id}`, {
                  state: buildContactReturnState(),
                })
              }
              onOpenCompany={(company) =>
                navigate(`/crm/companies?company_id=${company.id}`, {
                  state: buildContactReturnState(),
                })
              }
            />
          ) : null}
        </div>
      </section>

      <CrmMergeHistory copy={copy.entities.contacts.merge} mergeInfo={contact?.merge_info} />

      {editModalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleEditContactSubmit}>
            <div className="crm-modalHeader">
              <h2>{contactsCopy.editTitle}</h2>
              <button type="button" className="crm-iconButton" onClick={closeEditContact} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={contactsCopy.fields.first_name}
                value={form.first_name}
                onChange={(value) => handleFormFieldChange("first_name", value)}
              />
              <Field
                label={contactsCopy.fields.last_name}
                value={form.last_name}
                onChange={(value) => handleFormFieldChange("last_name", value)}
              />
              <Field
                label={contactsCopy.fields.email}
                value={form.email}
                type="email"
                onChange={(value) => handleFormFieldChange("email", value)}
              />
              <Field
                label={contactsCopy.fields.phone}
                value={form.phone}
                onChange={(value) => handleFormFieldChange("phone", value)}
              />
              <SelectField
                label={contactsCopy.fields.company_id}
                value={form.company_id}
                onChange={(value) => handleFormFieldChange("company_id", value)}
                options={[
                  { value: "", label: copy.common.noCompany },
                  ...companies.map((company) => ({ value: company.id, label: company.name })),
                ]}
              />
              <Field
                label={contactsCopy.fields.job_title}
                value={form.job_title}
                onChange={(value) => handleFormFieldChange("job_title", value)}
              />
              <Field
                label={contactsCopy.fields.department}
                value={form.department}
                onChange={(value) => handleFormFieldChange("department", value)}
              />
              <SelectField
                label={contactsCopy.fields.crm_status}
                value={form.crm_status}
                onChange={(value) => handleFormFieldChange("crm_status", value)}
                options={statusOptions}
              />
              <Field
                label={contactsCopy.fields.source}
                value={form.source}
                onChange={(value) => handleFormFieldChange("source", value)}
              />
              <Field
                label={contactsCopy.fields.notes}
                value={form.notes}
                textarea
                onChange={(value) => handleFormFieldChange("notes", value)}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeEditContact} disabled={editSaving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={editSaving}>
                {editSaving ? copy.common.saving : copy.common.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </main>
  );
}

function OverviewTab({ contact, copy, detailCopy, loading, onOpenCompany }) {
  if (loading && !contact) {
    return <div className="crm-emptyList">{copy.common.loading}</div>;
  }

  return (
    <div className="crm-detailGrid">
      <section className="crm-detailBlock">
        <h3>{detailCopy.sections.contact}</h3>
        <InfoGrid
          rows={[
            [copy.entities.contacts.fields.email, contact?.email || detailCopy.noEmail],
            [copy.entities.contacts.fields.phone, contact?.phone || detailCopy.noPhone],
            [copy.entities.contacts.fields.job_title, contact?.job_title || "-"],
            [copy.entities.contacts.fields.department, contact?.department || "-"],
            [copy.entities.contacts.fields.source, contact?.source || "-"],
            [detailCopy.fields.lastContacted, formatDateTime(contact?.last_contacted_at)],
          ]}
        />
      </section>

      <section className="crm-detailBlock">
        <h3>{detailCopy.sections.company}</h3>
        <InfoGrid
          rows={[
            [
              copy.entities.contacts.fields.company_id,
              contact?.company?.id ? (
                <button
                  type="button"
                  className="crm-inlineLinkButton"
                  onClick={() => onOpenCompany(contact.company)}
                >
                  {contact.company.name || copy.common.noCompany}
                </button>
              ) : (
                copy.common.noCompany
              ),
            ],
            [copy.entities.companies.fields.email, contact?.company?.email || "-"],
            [copy.entities.companies.fields.phone, contact?.company?.phone || "-"],
            [copy.entities.companies.fields.industry, contact?.company?.industry || "-"],
          ]}
        />
      </section>

      <section className="crm-detailBlock crm-detailBlock--wide">
        <h3>{detailCopy.sections.notes}</h3>
        <p className="crm-detailNotes">{contact?.notes || contact?.customer_description || detailCopy.noNotes}</p>
      </section>
    </div>
  );
}

function LeadListTab({
  leads,
  copy,
  detailCopy,
  loading,
  emptyLabel,
  fallbackLabel,
  onOpenLead,
  onOpenCompany,
}) {
  if (loading) {
    return <div className="crm-emptyList">{copy.common.loading}</div>;
  }

  if (!leads.length) {
    return <div className="crm-emptyList">{emptyLabel}</div>;
  }

  return (
    <div className="crm-detailList">
      {leads.map((lead) => {
        const leadMeta = [lead.company?.name, lead.source].filter(Boolean).join(" · ");
        const stageKey = lead.stage || "new";

        return (
          <article key={lead.id} className="crm-detailListRow">
            <div className="crm-detailListIcon">
              <FiFlag aria-hidden="true" />
            </div>
            <div className="crm-detailListMain">
              <button type="button" className="crm-detailListTitleButton" onClick={() => onOpenLead(lead)}>
                {lead.title || `${fallbackLabel} ${lead.id}`}
              </button>
              {lead.company?.id ? (
                <button type="button" className="crm-detailListSubButton" onClick={() => onOpenCompany(lead.company)}>
                  {leadMeta || copy.common.noCompany}
                </button>
              ) : (
                <span>{leadMeta || copy.common.noCompany}</span>
              )}
            </div>
            <div className="crm-detailListMeta">
              <span>
                <FiClock aria-hidden="true" />
                {lead.expected_close_date ? formatDate(lead.expected_close_date) : detailCopy.noDueDate}
              </span>
              <span className={`crm-statusPill crm-statusPill--${stageKey}`}>
                {copy.leadStages?.[stageKey] || stageKey || "-"}
              </span>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function ActivityTab({ conversations, copy, detailCopy, loading }) {
  if (loading) {
    return <div className="crm-emptyList">{copy.common.loading}</div>;
  }

  if (!conversations.length) {
    return <div className="crm-emptyList">{detailCopy.emptyActivity}</div>;
  }

  return (
    <div className="crm-detailList">
      {conversations.map((conversation) => (
        <article key={conversation.id} className="crm-detailListRow">
          <div className="crm-detailListIcon">
            <FiMessageSquare aria-hidden="true" />
          </div>
          <div className="crm-detailListMain">
            <strong>{conversation.subject}</strong>
            <span>
              {copy.conversationChannels?.[conversation.channel] || conversation.channel}
              {conversation.provider ? ` · ${conversation.provider}` : ""}
            </span>
          </div>
          <div className="crm-detailListMeta">
            <span>{formatDateTime(getConversationDate(conversation))}</span>
            <span className={`crm-statusPill crm-statusPill--${conversation.status || "open"}`}>
              {copy.entities.activity.status?.[conversation.status] || conversation.status}
            </span>
          </div>
        </article>
      ))}
    </div>
  );
}

function TaskContextLink({ task, copy, onOpenLead, onOpenCompany }) {
  if (task?.lead?.id) {
    return (
      <button type="button" className="crm-detailListSubButton" onClick={() => onOpenLead(task.lead)}>
        {task.lead.title || `Lead ${task.lead.id}`}
      </button>
    );
  }

  if (task?.company?.id) {
    return (
      <button type="button" className="crm-detailListSubButton" onClick={() => onOpenCompany(task.company)}>
        {task.company.name || copy.common.noCompany}
      </button>
    );
  }

  if (task?.conversation?.subject) return task.conversation.subject;
  return copy.common.noConversation;
}

function TasksTab({ tasks, copy, detailCopy, loading, onOpenLead, onOpenCompany }) {
  if (loading) {
    return <div className="crm-emptyList">{copy.common.loading}</div>;
  }

  if (!tasks.length) {
    return <div className="crm-emptyList">{detailCopy.emptyTasks}</div>;
  }

  return (
    <div className="crm-detailList">
      {tasks.map((task) => (
        <article key={task.id} className="crm-detailListRow">
          <div className="crm-detailListIcon">
            <FiCheckSquare aria-hidden="true" />
          </div>
          <div className="crm-detailListMain">
            <strong>{task.title}</strong>
            <span>
              {copy.taskTypes?.[task.task_type] || task.task_type}
              {" · "}
              <TaskContextLink
                task={task}
                copy={copy}
                onOpenLead={onOpenLead}
                onOpenCompany={onOpenCompany}
              />
            </span>
          </div>
          <div className="crm-detailListMeta">
            <span>
              <FiClock aria-hidden="true" />
              {task.due_at ? formatDateTime(task.due_at) : detailCopy.noDueDate}
            </span>
            <span className={`crm-statusPill crm-statusPill--${task.status || "open"}`}>
              {copy.taskStatuses?.[task.status] || task.status}
            </span>
          </div>
        </article>
      ))}
    </div>
  );
}

function InfoGrid({ rows }) {
  return (
    <dl className="crm-infoGrid">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Field({ label, value, onChange, type = "text", textarea = false }) {
  return (
    <label className={`crm-field ${textarea ? "crm-field--wide" : ""}`}>
      <span>{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={4} />
      ) : (
        <input type={type} value={value} onChange={(event) => onChange(event.target.value)} />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="crm-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={`${option.value}-${option.label}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
